import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import DigitalCard from "./pages/DigitalCard";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CampaignLanding from "./pages/CampaignLanding";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-[#F9F8F6]">
          <Header />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/promo" element={<CampaignLanding />} />
            <Route path="/queretaro" element={<CampaignLanding />} />
            <Route path="/terminos" element={<TermsConditions />} />
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            {/* Payment callback routes */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failure" element={<PaymentFailure />} />
            <Route path="/payment/pending" element={<PaymentPending />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            {/* Digital card route */}
            <Route path="/card/:membershipId" element={<DigitalCard />} />
          </Routes>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#1A3636',
                color: '#fff',
                border: 'none'
              }
            }}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Payment callback components
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function PaymentSuccess() {
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verificando tu pago...');
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('success');
      setMessage('Tu pago ha sido procesado. ¡Revisa tu correo para obtener tu tarjeta digital!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('success');
      setMessage('Tu pago está siendo procesado. Recibirás un correo con tu tarjeta digital.');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/payment/status/${sessionId}`, {
        withCredentials: true
      });
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('¡Pago exitoso! Revisa tu correo electrónico para obtener tu tarjeta digital SOS FindMed.');
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('La sesión de pago expiró. Por favor intenta de nuevo.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setStatus('success');
      setMessage('Tu pago está siendo verificado. Recibirás un correo de confirmación.');
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] pt-20 px-4">
      <div className="text-center max-w-md">
        {status === 'checking' ? (
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#1A3636] mb-4">Procesando...</h1>
          </>
        ) : status === 'success' ? (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#1A3636] mb-4">¡Pago Exitoso!</h1>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#1A3636] mb-4">Error</h1>
          </>
        )}
        <p className="text-[#5C6666] mb-6">{message}</p>
        <a 
          href="/dashboard" 
          className="inline-block bg-[#1A3636] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#0D1111] transition-colors"
        >
          Ir al Panel
        </a>
      </div>
    </div>
  );
}

function PaymentFailure() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] pt-20 px-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[#1A3636] mb-4">Pago Fallido</h1>
        <p className="text-[#5C6666] mb-6">
          Hubo un problema con tu pago. Por favor intenta de nuevo.
        </p>
        <a 
          href="/dashboard" 
          className="inline-block bg-[#C81D25] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#A6141B] transition-colors"
        >
          Volver al Panel
        </a>
      </div>
    </div>
  );
}

function PaymentPending() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] pt-20 px-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[#1A3636] mb-4">Pago Pendiente</h1>
        <p className="text-[#5C6666] mb-6">
          Tu pago está siendo procesado. Te notificaremos cuando se complete.
        </p>
        <a 
          href="/dashboard" 
          className="inline-block bg-[#1A3636] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#0D1111] transition-colors"
        >
          Ir al Panel
        </a>
      </div>
    </div>
  );
}

function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] pt-20 px-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[#1A3636] mb-4">Pago Cancelado</h1>
        <p className="text-[#5C6666] mb-6">
          No se realizó ningún cargo. Puedes intentar de nuevo cuando quieras.
        </p>
        <a 
          href="/dashboard" 
          className="inline-block bg-[#1A3636] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#0D1111] transition-colors"
        >
          Volver al Panel
        </a>
      </div>
    </div>
  );
}

export default App;
